<template>
    <div id="group-sessions">
        <div class="alert alert-info w-100" v-if="!groupMentorings.length && !loadingMentorings">
            Nenhuma mentoria em grupo encontrada. Cadastre uma mentoria do tipo “Em grupo” para publicar sessões de turma.
        </div>

        <template v-else>
            <b-form @submit.prevent="createSession" class="mb-4">
                <b-row>
                    <b-col md="3" class="mb-3">
                        <label for="group-session-mentoring">Mentoria em grupo</label>
                        <b-form-select
                            id="group-session-mentoring"
                            v-model="form.mentoring_id"
                            :options="mentoringOptions"
                            data-testid="group-session-mentoring-select"
                            required
                        />
                    </b-col>
                    <b-col md="3" class="mb-3">
                        <label for="group-session-cohort">Turma</label>
                        <b-form-select
                            id="group-session-cohort"
                            v-model="form.cohort_id"
                            :options="cohortOptions"
                            data-testid="group-session-cohort-select"
                            :disabled="!form.mentoring_id || loadingCohorts"
                            required
                        />
                    </b-col>
                    <b-col md="3" class="mb-3">
                        <label for="group-session-starts-at">Data e hora</label>
                        <b-form-input
                            id="group-session-starts-at"
                            v-model="form.starts_at"
                            type="datetime-local"
                            data-testid="group-session-starts-at"
                            required
                        />
                    </b-col>
                    <b-col md="2" class="mb-3">
                        <label for="group-session-section-content">Conteúdo (opcional)</label>
                        <b-form-input
                            id="group-session-section-content"
                            v-model="form.section_content_id"
                            type="number"
                            min="1"
                            placeholder="ID do conteúdo"
                            data-testid="group-session-section-content"
                        />
                    </b-col>
                    <b-col md="1" class="mb-3 d-flex align-items-end">
                        <b-button
                            type="submit"
                            variant="success"
                            class="w-100"
                            data-testid="group-session-create"
                            :disabled="saving || !form.cohort_id"
                        >
                            Criar
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>

            <div class="table-responsive">
                <table class="table table-bordered table-hover" data-testid="group-sessions-table">
                    <thead>
                    <tr>
                        <th>Data/hora</th>
                        <th>Link Meet</th>
                        <th>Participantes</th>
                        <th>Status</th>
                        <th style="width: 220px">Ações</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="!sessions.length && !loadingSessions">
                        <td colspan="5" class="text-center text-muted">
                            Nenhuma sessão publicada para esta mentoria.
                        </td>
                    </tr>
                    <tr v-for="session in sessions" :key="session.id">
                        <td>{{ formatStartsAt(session.starts_at) }}</td>
                        <td>
                            <a
                                v-if="session.meet_link"
                                :href="session.meet_link"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="group-session-meet-link"
                            >
                                Abrir Meet
                            </a>
                            <span v-else class="text-muted">—</span>
                        </td>
                        <td>
                            {{ (session.participants || []).length }}
                            <small
                                v-if="session.participants && session.participants.length"
                                class="d-block text-muted"
                            >
                                {{ participantNames(session) }}
                            </small>
                        </td>
                        <td>{{ statusLabel(session.status) }}</td>
                        <td>
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                class="mr-1 mb-1"
                                data-testid="group-session-reschedule"
                                :disabled="session.status == 2 || saving"
                                @click="openReschedule(session)"
                            >
                                Reagendar
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                class="mr-1 mb-1"
                                data-testid="group-session-notes"
                                :disabled="saving"
                                @click="openNotes(session)"
                            >
                                Notas
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-danger"
                                class="mb-1"
                                data-testid="group-session-cancel"
                                :disabled="session.status == 2 || saving"
                                @click="cancelSession(session)"
                            >
                                Cancelar
                            </b-button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <b-modal
            id="modal-reschedule-group-session"
            v-model="rescheduleModal"
            title="Reagendar sessão"
            ok-title="Salvar"
            cancel-title="Fechar"
            data-testid="group-session-reschedule-modal"
            @ok="onRescheduleOk"
        >
            <label for="group-session-reschedule-at">Nova data e hora</label>
            <b-form-input
                id="group-session-reschedule-at"
                v-model="rescheduleStartsAt"
                type="datetime-local"
                data-testid="group-session-reschedule-at"
                required
            />
        </b-modal>

        <b-modal
            id="modal-notes-group-session"
            v-model="notesModal"
            title="Notas da sessão"
            ok-title="Salvar privada"
            cancel-title="Fechar"
            data-testid="group-session-notes-modal"
            @ok="onSaveNotesOk"
        >
            <label for="group-session-private-note">Nota privada</label>
            <b-form-textarea
                id="group-session-private-note"
                v-model="notesForm.private_note"
                rows="3"
                data-testid="group-session-private-note"
                class="mb-3"
            />
            <label for="group-session-shared-note">Nota para a turma</label>
            <b-form-textarea
                id="group-session-shared-note"
                v-model="notesForm.shared_note"
                rows="3"
                data-testid="group-session-shared-note"
                class="mb-2"
                placeholder="Pauta, link de gravação, material…"
            />
            <b-form-checkbox v-model="notesForm.share" data-testid="group-session-share-note">
                Compartilhar com os mentorados
            </b-form-checkbox>
        </b-modal>
    </div>
</template>

<script>
import http from "@/http";
import {endLoading, startLoading} from "@/components/composables/spinners";
import {messageSwal} from "@/components/composables/messages";

const STATUS_LABELS = {
    1: 'Agendada',
    2: 'Cancelada',
    3: 'Realizada',
};

export default {
    name: 'GroupSessions',

    data() {
        return {
            groupMentorings: [],
            cohorts: [],
            sessions: [],
            loadingMentorings: false,
            loadingCohorts: false,
            loadingSessions: false,
            saving: false,
            form: {
                mentoring_id: null,
                cohort_id: null,
                starts_at: '',
                section_content_id: '',
            },
            rescheduleModal: false,
            rescheduleSessionId: null,
            rescheduleStartsAt: '',
            notesModal: false,
            notesSessionId: null,
            notesForm: {
                private_note: '',
                shared_note: '',
                share: false,
            },
        };
    },

    computed: {
        mentoringOptions() {
            const options = this.groupMentorings.map(m => ({
                value: m.id,
                text: m.title,
            }));
            return [{value: null, text: 'Selecione a mentoria', disabled: true}, ...options];
        },

        cohortOptions() {
            const options = this.cohorts.map(c => ({
                value: c.id,
                text: `${c.name} (${c.status === 'open' ? 'aberta' : 'fechada'})`,
            }));
            return [{value: null, text: 'Selecione a turma', disabled: true}, ...options];
        },
    },

    watch: {
        'form.mentoring_id'(id) {
            this.form.cohort_id = null;
            this.cohorts = [];
            this.sessions = [];
            if (id) {
                this.fetchCohorts();
            }
        },
        'form.cohort_id'(id) {
            if (id) {
                this.fetchSessions();
            } else {
                this.sessions = [];
            }
        },
    },

    methods: {
        isGroupMentoring(m) {
            if (!m) return false;
            return !!(m.limited_vacancies || m.type_id == 2 || m.type_id == '2');
        },

        statusLabel(status) {
            return STATUS_LABELS[Number(status)] || '—';
        },

        participantNames(session) {
            return (session.participants || [])
                .map(p => p.name)
                .filter(Boolean)
                .join(', ');
        },

        formatStartsAt(value) {
            if (!value) return '—';
            const date = new Date(value.replace(' ', 'T'));
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString('pt-BR');
        },

        toApiDateTime(localValue) {
            if (!localValue) return null;
            // datetime-local → ISO sem timezone explícito (API aceita date)
            return localValue.length === 16 ? `${localValue}:00` : localValue;
        },

        toDatetimeLocal(value) {
            if (!value) return '';
            const date = new Date(value.replace(' ', 'T'));
            if (Number.isNaN(date.getTime())) return '';
            const pad = n => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
        },

        getMentorings() {
            this.loadingMentorings = true;
            http.get('mentorings?paginated=false')
                .then(response => {
                    const list = Array.isArray(response.data)
                        ? response.data
                        : (response.data.data || []);
                    this.groupMentorings = list.filter(m => this.isGroupMentoring(m));
                    if (this.groupMentorings.length && !this.form.mentoring_id) {
                        this.form.mentoring_id = this.groupMentorings[0].id;
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.loadingMentorings = false;
                });
        },

        fetchCohorts() {
            if (!this.form.mentoring_id) return;
            this.loadingCohorts = true;
            http.get(`mentorings/${this.form.mentoring_id}/cohorts`)
                .then(response => {
                    this.cohorts = Array.isArray(response.data) ? response.data : [];
                    if (this.cohorts.length && !this.form.cohort_id) {
                        this.form.cohort_id = this.cohorts[0].id;
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                    this.cohorts = [];
                })
                .finally(() => {
                    this.loadingCohorts = false;
                });
        },

        fetchSessions() {
            if (!this.form.mentoring_id || !this.form.cohort_id) return;
            this.loadingSessions = true;
            startLoading('group-sessions');
            http.get('group-sessions', {
                params: {
                    mentoring_id: this.form.mentoring_id,
                    cohort_id: this.form.cohort_id,
                },
            })
                .then(response => {
                    this.sessions = Array.isArray(response.data) ? response.data : [];
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                    this.sessions = [];
                })
                .finally(() => {
                    this.loadingSessions = false;
                    endLoading('group-sessions');
                });
        },

        createSession() {
            if (!this.form.mentoring_id || !this.form.cohort_id || !this.form.starts_at) {
                this.$notifyError('Preencha mentoria, turma e data/hora.');
                return;
            }

            const payload = {
                mentoring_id: Number(this.form.mentoring_id),
                cohort_id: Number(this.form.cohort_id),
                starts_at: this.toApiDateTime(this.form.starts_at),
            };

            if (this.form.section_content_id) {
                payload.section_content_id = Number(this.form.section_content_id);
            }

            this.saving = true;
            startLoading('group-sessions', 'group-session-create');
            http.post('group-sessions', payload)
                .then(response => {
                    this.$notifySuccess(response.data?.message || 'Sessão criada com sucesso!');
                    this.form.starts_at = '';
                    this.form.section_content_id = '';
                    this.fetchSessions();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.saving = false;
                    endLoading('group-sessions', 'group-session-create');
                });
        },

        openReschedule(session) {
            this.rescheduleSessionId = session.id;
            this.rescheduleStartsAt = this.toDatetimeLocal(session.starts_at);
            this.rescheduleModal = true;
        },

        openNotes(session) {
            this.notesSessionId = session.id;
            this.notesForm = {
                private_note: session.private_note || '',
                shared_note: session.shared_note || '',
                share: !!session.shared_note,
            };
            this.notesModal = true;
        },

        onSaveNotesOk(bvModalEvt) {
            bvModalEvt.preventDefault();
            if (!this.notesSessionId) return;

            this.saving = true;
            http.put(`group-sessions/${this.notesSessionId}/notes`, {
                private_note: this.notesForm.private_note,
                shared_note: this.notesForm.shared_note,
                share: !!this.notesForm.share,
            })
                .then(() => {
                    this.$notifySuccess(
                        this.notesForm.share
                            ? 'Notas salvas e compartilhadas com a turma'
                            : 'Nota privada salva'
                    );
                    this.notesModal = false;
                    this.fetchSessions();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data?.message || e.response?.data || e);
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        onRescheduleOk(bvModalEvt) {
            bvModalEvt.preventDefault();
            this.confirmReschedule();
        },

        confirmReschedule() {
            if (!this.rescheduleSessionId || !this.rescheduleStartsAt) {
                this.$notifyError('Informe a nova data/hora.');
                return;
            }

            this.saving = true;
            http.put(`group-sessions/${this.rescheduleSessionId}`, {
                starts_at: this.toApiDateTime(this.rescheduleStartsAt),
            })
                .then(() => {
                    this.$notifySuccess('Sessão reagendada com sucesso!');
                    this.rescheduleModal = false;
                    this.fetchSessions();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        async cancelSession(session) {
            const confirmed = await messageSwal(
                'Cancelar sessão',
                'Tem certeza que deseja cancelar esta sessão? Os participantes serão notificados.',
                'warning'
            );
            if (!confirmed) return;

            this.saving = true;
            http.delete(`group-sessions/${session.id}`)
                .then(() => {
                    this.$notifySuccess('Sessão cancelada com sucesso!');
                    this.fetchSessions();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },

    mounted() {
        this.getMentorings();
    },
};
</script>
